import { NodeSelection } from 'prosemirror-state';
const raf = (fn) => requestAnimationFrame(fn);
export const createShowToolbarFunc = (editor, toolbar, { zIndex, placement = 'bottom', margin = 10 } = {}) => {
    let init = false;
    let width = 0;
    let height = 0;
    let lastpos;
    const ob = new ResizeObserver((entries) => {
        if (!init) {
            return;
        }
        const box = entries[0].contentRect;
        const newWidth = box.width;
        const newHeight = box.height;
        const isHide = !newWidth || !newHeight;
        const needUpdate = newWidth !== width || newHeight !== height;
        if (!isHide && needUpdate) {
            width = newWidth;
            height = newHeight;
            show(lastpos);
        }
    });
    const show = (pos) => {
        if (editor.isReadOnly()) {
            return;
        }
        lastpos = pos;
        const domRect = editor.view.dom.getBoundingClientRect();
        if (!init) {
            Object.assign(toolbar.style, {
                visibility: 'hidden',
                display: 'flex',
                top: '0px',
                left: '0px',
                'max-width': `${domRect.width}px`,
            });
            // fix firefox bug
            toolbar.querySelectorAll('svg').forEach((el) => {
                el.style.maxHeight = '100%';
            });
            raf(() => {
                toolbar.style.visibility = 'visible';
                init = true;
                const rect = toolbar.getBoundingClientRect();
                width = rect.width;
                height = rect.height;
                show(pos);
                ob.observe(toolbar);
            });
        }
        else {
            const coords = editor.view.coordsAtPos(pos);
            const { documentElement: html, body } = document;
            const scrollTop = html.scrollTop || body.scrollTop;
            const scrollLeft = html.scrollLeft || body.scrollLeft;
            let top = coords.bottom + scrollTop + margin;
            let left = coords.left + scrollLeft;
            if (coords.left + width > domRect.right) {
                left = domRect.right - width;
            }
            if (placement === 'bottom') {
                const bottom = top + height;
                if (bottom > html.clientHeight + scrollTop) {
                    top = coords.top - height + scrollTop - margin;
                }
            }
            else if (placement === 'top') {
                top = coords.top + scrollTop - margin - height;
                // 判断顶部被挡住后，放到下面
                if (top < scrollTop) {
                    top = coords.bottom + scrollTop + margin;
                }
            }
            Object.assign(toolbar.style, {
                display: 'flex',
                top: `${top}px`,
                left: `${left}px`,
                'z-index': zIndex || 1,
            });
        }
    };
    show.clean = () => {
        if (ob) {
            ob.unobserve(toolbar);
            ob.disconnect();
        }
    };
    return show;
};
export const createToolbar = (toolbar, context) => {
    const eltWrapper = document.createElement('div');
    eltWrapper.className = 'exeditor-toolbar';
    if (context.theme) {
        eltWrapper.dataset.theme = context.theme;
    }
    if (context.in) {
        eltWrapper.dataset.in = context.in;
    }
    eltWrapper.onmousedown = (e) => {
        if (e.target === e.currentTarget) {
            e.preventDefault();
        }
    };
    const appendToRow = (toolbar, wrapper) => {
        var _a;
        // parse items string
        (_a = toolbar
            .trim()
            .match(/\S+\([^)]+\)|\S+/g)) === null || _a === void 0 ? void 0 : _a.forEach((piece) => {
            let name = piece;
            let params = '';
            if (piece.includes('(')) {
                const parenthesesIndex = piece.indexOf('(');
                name = piece.slice(0, parenthesesIndex);
                params = piece.slice(parenthesesIndex + 1, -1);
            }
            const itemRenderer = context.getItemRenderer(name);
            if (!itemRenderer) {
                return;
            }
            const eltItem = itemRenderer(context, params);
            eltItem.dataset.toolbarItemOf = name;
            wrapper.appendChild(eltItem);
        });
    };
    if (Array.isArray(toolbar)) {
        eltWrapper.classList.add('exeditor-toolbar--multiline');
        toolbar.forEach((items) => {
            const eltRow = document.createElement('div');
            eltRow.className = 'exeditor-toolbar__row';
            appendToRow(items, eltRow);
            eltWrapper.appendChild(eltRow);
        });
    }
    else {
        appendToRow(toolbar, eltWrapper);
    }
    return eltWrapper;
};
export const createPopoverToolbar = (toolbar, context, toolbarRoot) => {
    const { editor } = context;
    const elementToolbar = createToolbar(toolbar, { ...context, in: 'popover' });
    elementToolbar.style.display = 'none';
    const container = document.body;
    container.appendChild(elementToolbar);
    const hide = () => {
        if (elementToolbar.style.display !== 'none') {
            elementToolbar.style.display = 'none';
        }
    };
    const toolbarRootZIndex = toolbarRoot
        ? parseInt(getComputedStyle(toolbarRoot).zIndex, 10)
        : 0;
    const show = createShowToolbarFunc(context.editor, elementToolbar, {
        zIndex: toolbarRootZIndex,
    });
    let timer = 0;
    const clearTimer = () => {
        if (timer) {
            clearTimeout(timer);
            timer = 0;
        }
    };
    const checkSelection = (selection) => {
        // 未选中或者选中 Node
        if (selection.empty || selection instanceof NodeSelection) {
            clearTimer();
            hide();
            return false;
        }
        return true;
    };
    const handleUpdatePosition = () => {
        const { selection } = editor.view.state;
        if (!checkSelection(selection)) {
            return;
        }
        show(selection.head);
    };
    // 点击其他 toolbar 时候隐藏
    toolbarRoot === null || toolbarRoot === void 0 ? void 0 : toolbarRoot.addEventListener('click', hide);
    editor.on('beforedestroy', () => {
        show.clean();
        clearTimer();
        toolbarRoot === null || toolbarRoot === void 0 ? void 0 : toolbarRoot.removeEventListener('click', hide);
        elementToolbar.remove();
    });
    editor.on('blur', () => {
        clearTimer();
        hide();
    });
    editor.on('resize', handleUpdatePosition);
    editor.on('scroll', handleUpdatePosition);
    editor.on('change', (event) => {
        const { selection } = event.state;
        if (!checkSelection(selection)) {
            return;
        }
        if (event.selectionSet) {
            hide();
            clearTimer();
            timer = window.setTimeout(() => {
                show(selection.head);
            }, 300);
        }
        else {
            // 选区未改变
            show(selection.head);
        }
    });
    return elementToolbar;
};
//# sourceMappingURL=toolbar.js.map